import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../contexts/LanguageContext';
import CreateClassroomModuleModal from './CreateClassroomModuleModal';
import { backendUrl } from '../config';
import './ClassroomsList.css';

const ClassroomsList = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [classrooms, setClassrooms] = useState([]);
  const [classroomModules, setClassroomModules] = useState({}); // { classroomId: [modules] }
  const [classroomRoles, setClassroomRoles] = useState({}); // { classroomId: 'Teacher' | 'Co-Teacher' | 'Student' }
  const [classroomWordUsage, setClassroomWordUsage] = useState({}); // { classroomId: { words_used, words_remaining, monthly_limit } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [deletingClassroomId, setDeletingClassroomId] = useState(null);
  const [leavingClassroomId, setLeavingClassroomId] = useState(null);
  const lastFetchRef = useRef(null);

  const fetchClassrooms = useCallback(async (currentUserId) => {
    setLoading(true);
    setError('');

    try {
      if (!currentUserId) {
        setLoading(false);
        return;
      }

      // Step 1: Fetch classroom_members to get user's role in each classroom
      const { data: memberData, error: memberError } = await supabase
        .from('classroom_members')
        .select('classroom_id, class_roll')
        .eq('user_id', currentUserId);

      if (memberError) {
        throw memberError;
      }

      // Build map of classroom roles and collect classroom IDs
      const rolesMap = {};
      const classroomIds = [];
      if (memberData && memberData.length > 0) {
        memberData.forEach(member => {
          rolesMap[member.classroom_id] = member.class_roll;
          classroomIds.push(member.classroom_id);
        });
      }

      // If no classrooms, set empty and return early
      if (classroomIds.length === 0) {
        setClassrooms([]);
        setClassroomRoles({});
        setClassroomModules({});
        setLoading(false);
        return;
      }

      // Step 2 & 3: Fetch classrooms and modules in parallel (they don't depend on each other)
      const [classroomsResult, modulesResult] = await Promise.all([
        supabase
          .from('classrooms')
          .select('id, name, created_at, join_code, password')
          .in('id', classroomIds)
          .order('created_at', { ascending: false }),
        supabase
          .from('modules_new')
          .select('id, name, position, from_lang, to_lang, num_groups, num_words, classroom_id')
          .in('classroom_id', classroomIds)
          .order('position', { ascending: false })
      ]);

      if (classroomsResult.error) {
        throw classroomsResult.error;
      }

      const allClassrooms = classroomsResult.data || [];

      // Sort by created_at
      allClassrooms.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setClassrooms(allClassrooms);
      setClassroomRoles(rolesMap);

      // Fetch word usage for classrooms where user is Teacher/Co-Teacher
      const adminClassroomIds = Object.keys(rolesMap).filter(
        id => rolesMap[id] === 'Teacher' || rolesMap[id] === 'Co-Teacher'
      );

      if (adminClassroomIds.length > 0) {
        const usagePromises = adminClassroomIds.map(async (classroomId) => {
          try {
            const { data, error } = await supabase.rpc('get_classroom_word_usage', {
              p_classroom_id: classroomId,
              p_user_id: currentUserId
            });

            if (error || (data && data.error)) {
              console.error(`Error fetching word usage for classroom ${classroomId}:`, error || data.error);
              return { classroomId, usage: null };
            }

            return { classroomId, usage: data };
          } catch (err) {
            console.error(`Error fetching word usage for classroom ${classroomId}:`, err);
            return { classroomId, usage: null };
          }
        });

        const usageResults = await Promise.all(usagePromises);
        const usageMap = {};
        usageResults.forEach(({ classroomId, usage }) => {
          if (usage) {
            usageMap[classroomId] = usage;
          }
        });
        setClassroomWordUsage(usageMap);
      }

      // Process modules data
      if (modulesResult.error) {
        console.error('Error fetching classroom modules:', modulesResult.error);
        setClassroomModules({});
      } else {
        // Group modules by classroom_id
        const grouped = {};
        (modulesResult.data || []).forEach(module => {
          if (!grouped[module.classroom_id]) {
            grouped[module.classroom_id] = [];
          }
          grouped[module.classroom_id].push(module);
        });
        setClassroomModules(grouped);
      }
    } catch (err) {
      console.error('Error fetching classrooms:', err);
      setError(err.message || t('errorFetchingClassrooms') || 'Error fetching classrooms');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const initialize = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUserId(session?.user?.id);
      if (session?.user?.id) {
        await fetchClassrooms(session.user.id);
        lastFetchRef.current = Date.now();
      }
    };
    initialize();
  }, [fetchClassrooms]);

  // Refetch when navigating back to dashboard (location.pathname === '/')
  useEffect(() => {
    if (location.pathname === '/' && userId) {
      // Only refetch if it's been more than 1 second since last fetch (avoid excessive refetches)
      const now = Date.now();
      if (!lastFetchRef.current || (now - lastFetchRef.current) > 1000) {
        fetchClassrooms(userId);
        lastFetchRef.current = now;
      }
    }
  }, [location.pathname, userId, fetchClassrooms]);

  // Also refetch when window comes back into focus (user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (userId && document.visibilityState === 'visible') {
        const now = Date.now();
        // Only refetch if it's been more than 5 seconds since last fetch
        if (!lastFetchRef.current || (now - lastFetchRef.current) > 5000) {
          fetchClassrooms(userId);
          lastFetchRef.current = now;
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [userId, fetchClassrooms]);

  // Listen for custom event to refresh classrooms (triggered when classroom data changes)
  useEffect(() => {
    const handleRefresh = () => {
      if (userId) {
        const now = Date.now();
        // Only refetch if it's been more than 500ms since last fetch (avoid excessive refetches)
        if (!lastFetchRef.current || (now - lastFetchRef.current) > 500) {
          fetchClassrooms(userId);
          lastFetchRef.current = now;
        }
      }
    };

    window.addEventListener('refreshClassrooms', handleRefresh);

    return () => {
      window.removeEventListener('refreshClassrooms', handleRefresh);
    };
  }, [userId, fetchClassrooms]);

  if (loading) {
    return (
      <div className="classrooms-section">
        <div className="classrooms-section-header">
          <h2>{t('myClassrooms') || 'My Classrooms'}</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          {t('loading') || 'Loading...'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="classrooms-section">
        <div className="classrooms-section-header">
          <h2>{t('myClassrooms') || 'My Classrooms'}</h2>
        </div>
        <div style={{ color: 'red', textAlign: 'center', padding: '1rem' }}>
          {error}
        </div>
      </div>
    );
  }

  if (!classrooms || classrooms.length === 0) {
    return null; // Don't show section if no classrooms
  }

  const handleDeleteClassroom = async (classroomId, classroomName) => {
    const confirmed = window.confirm(
      t('confirmDeleteClassroom') ||
      `Are you sure you want to delete "${classroomName}"? This will permanently delete the classroom, all modules, word groups, words, and all student progress. This cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingClassroomId(classroomId);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${backendUrl}/delete-classroom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          classroomId: classroomId,
          accessToken: session?.access_token
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete classroom');
      }

      // Refresh classrooms list
      if (userId) {
        await fetchClassrooms(userId);
      }
    } catch (err) {
      console.error('Error deleting classroom:', err);
      setError(err.message || t('errorDeletingClassroom') || 'Error deleting classroom');
    } finally {
      setDeletingClassroomId(null);
    }
  };

  const handleLeaveClassroom = async (classroomId, classroomName) => {
    const confirmed = window.confirm(
      t('confirmLeaveClassroom') ||
      `Are you sure you want to leave "${classroomName}"? This will delete all your progress in this classroom.`
    );

    if (!confirmed) {
      return;
    }

    setLeavingClassroomId(classroomId);
    setError('');

    try {
      const { error: leaveError } = await supabase.rpc('leave_classroom', {
        classroom_uuid: classroomId
      });

      if (leaveError) {
        throw leaveError;
      }

      // Refresh classrooms list
      if (userId) {
        await fetchClassrooms(userId);
      }
    } catch (err) {
      console.error('Error leaving classroom:', err);
      setError(err.message || t('errorLeavingClassroom') || 'Error leaving classroom');
    } finally {
      setLeavingClassroomId(null);
    }
  };

  return (
    <div className="classrooms-section">
      <div className="classrooms-section-header">
        <h2>{t('myClassrooms') || 'My Classrooms'}</h2>
      </div>

      <div className="classrooms-grid">
        {classrooms.map(classroom => {
          const modules = classroomModules[classroom.id] || [];
          const userRole = classroomRoles[classroom.id]; // 'Teacher', 'Co-Teacher', or 'Student'
          const isAdmin = userRole === 'Teacher' || userRole === 'Co-Teacher'; // Can create modules and view class

          return (
            <div key={classroom.id} className="classroom-card">
              <div className="classroom-card-header">
                <div>
                  <h3 className="classroom-card-title">{classroom.name}</h3>
                  <div style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <span>
                      <strong>{t('classroomCode') || 'Code'}:</strong> <code style={{ fontFamily: 'monospace', background: '#f0f0f0', padding: '2px 6px', borderRadius: '3px' }}>{classroom.join_code}</code>
                    </span>
                    <span>
                      <strong>{t('classroomPassword') || 'Password'}:</strong> <code style={{ fontFamily: 'monospace', background: '#f0f0f0', padding: '2px 6px', borderRadius: '3px' }}>{classroom.password}</code>
                    </span>
                  </div>
                </div>
                {userRole && (
                  <span className="classroom-badge">
                    {userRole === 'Teacher' ? (t('teacher') || 'Teacher') :
                      userRole === 'Co-Teacher' ? (t('coTeacher') || 'Co-Teacher') :
                        (t('student') || 'Student')}
                  </span>
                )}
              </div>

              {isAdmin && (
                <>
                  {classroomWordUsage[classroom.id] && (
                    <div style={{
                      fontSize: '0.85em',
                      color: '#666',
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px'
                    }}>
                      <strong>{t('wordGenerationLimit') || 'Word Generation Limit'}:</strong>
                      <div style={{ marginTop: '0.25rem' }}>
                        {classroomWordUsage[classroom.id].words_used || 0} / {classroomWordUsage[classroom.id].monthly_limit || 1200} {t('wordsThisMonth') || 'words this month'}
                        {classroomWordUsage[classroom.id].words_remaining !== undefined && (
                          <span style={{
                            color: classroomWordUsage[classroom.id].words_remaining > 0 ? '#28a745' : '#dc3545',
                            marginLeft: '0.5rem'
                          }}>
                            ({classroomWordUsage[classroom.id].words_remaining} {t('remaining') || 'remaining'})
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <Link
                    to={`/classroom/${classroom.id}/view`}
                    className="btn btn-secondary btn-sm"
                    style={{
                      marginBottom: '0.5rem',
                      width: '100%',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textDecoration: 'none'
                    }}
                  >
                    {t('viewClass') || 'View Class'}
                  </Link>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setSelectedClassroom(classroom);
                      setShowCreateModal(true);
                    }}
                    style={{
                      marginBottom: '1rem',
                      width: '100%'
                    }}
                  >
                    + {t('createModule') || 'Create Module'}
                  </button>
                </>
              )}

              {modules.length > 0 ? (
                <div className="classroom-modules">
                  {modules.map(module => (
                    <Link
                      key={module.id}
                      to={`/module/${module.id}`}
                      className="classroom-module-link"
                    >
                      <div className="classroom-module-item">
                        <span className="classroom-module-name">{module.name}</span>
                        <span className="classroom-module-meta">
                          {module.num_groups || 0} {t('groups') || 'groups'} • {module.num_words || 0} {t('words') || 'words'}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="classroom-empty">
                  <p>{t('noModulesInClassroom') || 'No modules in this classroom yet'}</p>
                </div>
              )}

              {/* Delete/Leave buttons below modules */}
              {userRole === 'Teacher' && (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteClassroom(classroom.id, classroom.name)}
                  disabled={deletingClassroomId === classroom.id}
                  style={{
                    marginTop: '1rem',
                    width: '100%',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {deletingClassroomId === classroom.id
                    ? (t('deleting') || 'Deleting...')
                    : (t('deleteClassroom') || 'Delete Classroom')}
                </button>
              )}
              {(userRole === 'Co-Teacher' || userRole === 'Student') && (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleLeaveClassroom(classroom.id, classroom.name)}
                  disabled={leavingClassroomId === classroom.id}
                  style={{
                    marginTop: '1rem',
                    width: '100%',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {leavingClassroomId === classroom.id
                    ? (t('leaving') || 'Leaving...')
                    : (t('leaveClassroom') || 'Leave Classroom')}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {showCreateModal && selectedClassroom && (
        <CreateClassroomModuleModal
          classroomId={selectedClassroom.id}
          classroomName={selectedClassroom.name}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedClassroom(null);
          }}
          onSuccess={() => {
            // Refresh classrooms to show new module
            if (userId) {
              fetchClassrooms(userId);
            }
          }}
        />
      )}
    </div>
  );
};

export default ClassroomsList;

